import { CSSProperties, FC, KeyboardEvent, MouseEvent, ReactNode, useEffect, useRef } from 'react';
import { useIframeDialogClamp } from 'features/common/hooks/useIframeDialogClamp';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import styles from './PunchupDialogue.module.css';

/** Default container max-width in px — must equal the `.container` `max-width` in PunchupDialogue.module.css. */
export const DEFAULT_MAX_WIDTH = 400;

/** Size of the loading-overlay PunchupSpinner shared by PunchupDialogue and PunchupConfirmDialog. */
export const DIALOG_SPINNER_SIZE = 32;

/** id wired between the container's aria-labelledby and the DialogTitle h2 for SR announcement. */
const DIALOG_TITLE_ID = 'punchup-dialog-title';

/** Tab-focusable elements query used by the dialog focus trap. */
const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface PunchupDialogueProps {
	children: ReactNode;
	/** Maximum width of the dialog container in px. Default 400. Use 600 for feed/form dialogs, 900 for wide pickers. */
	maxWidth?: number;
	/** Covers the dialog content with a semi-transparent overlay + 32px spinner. Set once the API call is dispatched; clear on success or error. */
	isLoading?: boolean;
	/** When true (with isLoading), disables backdrop click and ESC close. Use for financial mutations (Cancel Ticket, Refund). */
	isDestructive?: boolean;
	/** Called when the user clicks the backdrop or presses ESC. Blocked when isLoading + isDestructive. */
	onClose?: () => void;
}

export const PunchupDialogue: FC<PunchupDialogueProps> = ({
	children,
	maxWidth = DEFAULT_MAX_WIDTH,
	isLoading = false,
	isDestructive = false,
	onClose,
}) => {
	const { isInIframe, overlayStyle, containerStyle } = useIframeDialogClamp();
	const closeBlocked = isLoading && isDestructive;

	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isInIframe) return;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isInIframe]);

	useEffect(() => {
		if (!onClose) return;
		const handleKeyDown = (e: globalThis.KeyboardEvent) => {
			if (e.key === 'Escape' && !closeBlocked) onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onClose, closeBlocked]);

	/* Move focus into the dialog on open and restore it to the trigger on close. */
	useEffect(() => {
		const previousFocus = document.activeElement as HTMLElement | null;
		containerRef.current?.focus();
		return () => previousFocus?.focus?.();
	}, []);

	/* Trap Tab / Shift+Tab within the dialog so keyboard users cannot reach the inert background. */
	const handleContainerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		if (e.key !== 'Tab') return;
		const container = containerRef.current;
		if (!container) return;
		const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
		if (focusable.length === 0) {
			e.preventDefault();
			container.focus();
			return;
		}
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;
		if (e.shiftKey && (active === first || active === container)) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && active === last) {
			e.preventDefault();
			first.focus();
		}
	};

	const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
		if (e.target !== e.currentTarget) return;
		if (!closeBlocked) onClose?.();
	};

	const overlayClass = [styles.overlay, isInIframe ? styles.iframeDialogue : ''].filter(Boolean).join(' ');

	// Merge the caller's non-default maxWidth with the iframe band-cap (containerStyle.maxHeight). Both
	// are optional, so the inline style stays absent (CSS in charge) when neither applies.
	const widthStyle = maxWidth !== DEFAULT_MAX_WIDTH ? { maxWidth } : undefined;
	const mergedContainerStyle: CSSProperties | undefined =
		widthStyle || containerStyle ? { ...widthStyle, ...containerStyle } : undefined;

	return (
		<div className={overlayClass} style={overlayStyle} onClick={handleBackdropClick}>
			<div
				ref={containerRef}
				className={styles.container}
				style={mergedContainerStyle}
				role="dialog"
				aria-modal="true"
				aria-labelledby={DIALOG_TITLE_ID}
				aria-busy={isLoading || undefined}
				tabIndex={-1}
				onKeyDown={handleContainerKeyDown}
			>
				{isLoading && (
					<div className={styles.loadingOverlay}>
						<PunchupSpinner size={DIALOG_SPINNER_SIZE} />
					</div>
				)}
				{children}
			</div>
		</div>
	);
};

/** Icon circle rendered at the top of a dialog. `variant="primary"` → orange, `variant="destructive"` → red. */
export const DialogIcon: FC<{ children: ReactNode; variant?: 'primary' | 'destructive' }> = ({
	children,
	variant = 'primary',
}) => (
	<div
		className={[styles.iconCircle, variant === 'destructive' ? styles.iconCircleDestructive : '']
			.filter(Boolean)
			.join(' ')}
	>
		{children}
	</div>
);

/** Dialog heading below the icon. Same size and weight as PunchupConfirmDialog / PunchupModal titles. */
export const DialogTitle: FC<{ children: ReactNode }> = ({ children }) => (
	<h2 id={DIALOG_TITLE_ID} className={styles.dialogTitle}>
		{children}
	</h2>
);

/** Muted subtitle rendered directly below DialogTitle. Only render when the value is non-empty. */
export const DialogSubtitle: FC<{ children: ReactNode }> = ({ children }) => (
	<p className={styles.dialogSubtitle}>{children}</p>
);

/** Equal-width action button row. Uses CSS flex so all buttons share equal width (max 160px each) regardless of label length. */
export const ButtonGroup: FC<{ children: ReactNode }> = ({ children }) => (
	<div className={styles.buttonGroup}>{children}</div>
);
